import { DesktopDatePicker } from '@mui/lab';
import { Box, Modal, TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { DateRange as SelectedDateRange } from '../../store';
import { DateTimeFormat } from '@src/app/utils/GenericUtils';
import WO2Button from '@components/button/Button';
import PreDefinedDatesSelect from '@components/dateRange/DateRangePreDefinedDates';

interface Props {
  inceptionDate?: string;
  dateRange: SelectedDateRange;
  isDateOnly: boolean;
  open: boolean;
  setDateRange: (dateRange: SelectedDateRange) => void;
  onClose: () => void;
}

export default function DateRangePickerModal(props: Props): JSX.Element {
  const [dateFromError, setDateFromError] = useState<boolean>(false);
  const [dateToError, setDateToError] = useState<boolean>(false);
  const [dateTo, setDateTo] = useState<DateTime | null>(DateTime.fromISO(props.dateRange.dateTo));
  const [dateFrom, setDateFrom] = useState<DateTime | null>(DateTime.fromISO(props.dateRange.dateFrom));
  const [dateRangeType, setDateRangeType] = useState<string | null>('');

  const { isDateOnly } = props;

  const handleDateChange = (materialUiPickersDate: DateTime | null, setFunction: (date: DateTime | null) => void) => {
    setFunction(materialUiPickersDate ?? null);
  };

  const closeDatePicker = () => {
    if (dateFrom && dateTo && !dateFromError && !dateToError) {
      props.setDateRange({
        dateFrom: dateFrom.toISODate(),
        dateTo: dateTo.toISODate(),
        dateRangeType: dateRangeType,
      });

      props.onClose();
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
      }}
    >
      <Box display="flex" maxWidth={500} borderRadius="5px" bgcolor="#ffffff" flexDirection="column" padding={4} gap={4}>
        <PreDefinedDatesSelect
          dateTo={dateTo}
          setDateTo={setDateTo}
          dateRangeType={dateRangeType}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          inceptionDate={props.inceptionDate}
          isDateOnly={isDateOnly}
          setDateRangeType={setDateRangeType}
        />
        {props.isDateOnly ? (
          <Box display="flex" flexDirection="column">
            <DesktopDatePicker
              inputFormat={DateTimeFormat.Short.displayName}
              label="Date"
              value={dateTo}
              onChange={(e) => {
                setDateToError(false);
                handleDateChange(e, setDateTo);
              }}
              OpenPickerButtonProps={{
                'aria-label': 'change date',
              }}
              maxDate={DateTime.now()}
              onError={(error) => setDateToError(error !== null)}
              renderInput={(params) => <TextField {...params} variant="outlined" />}
            />
          </Box>
        ) : (
          <Box display="flex" sx={{ flexDirection: { xs: 'column', sm: 'row' } }} gap={2}>
            <DesktopDatePicker
              inputFormat={DateTimeFormat.Short.displayName}
              label="From date"
              value={dateFrom}
              onChange={(e) => {
                setDateFromError(false);
                handleDateChange(e, setDateFrom);
              }}
              OpenPickerButtonProps={{
                'aria-label': 'change date',
              }}
              maxDate={dateTo}
              onError={(error) => {
                setDateFromError(error !== null);
              }}
              renderInput={(params) => <TextField sx={{ flex: 1 }} {...params} variant="outlined" />}
            />
            <DesktopDatePicker
              inputFormat={DateTimeFormat.Short.displayName}
              label="To date"
              value={dateTo}
              onChange={(e) => {
                setDateToError(false);
                handleDateChange(e, setDateTo);
              }}
              OpenPickerButtonProps={{
                'aria-label': 'change date',
              }}
              minDate={dateFrom}
              maxDate={DateTime.now()}
              onError={(error) => {
                setDateToError(error !== null);
              }}
              renderInput={(params) => <TextField sx={{ flex: 1 }} {...params} variant="outlined" />}
            />
          </Box>
        )}
        <Box display="flex" justifyContent="flex-end">
          <WO2Button
            variant="contained"
            color={!dateFromError && !dateToError ? 'primary' : 'inherit'}
            sx={{ height: '2.625rem', textTransform: 'none' }}
            onClick={closeDatePicker}
          >
            APPLY
          </WO2Button>
        </Box>
      </Box>
    </Modal>
  );
}
